import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import {
  MoreVertical,
  Pencil,
  Copy,
  Zap,
  ZapOff,
  Trash2,
  Pause,
  Play,
} from 'lucide-react'
import { cn } from '../UI/cn'
import { useModalDepth } from '../UI/Modal/ModalPrimitive'
import { getDropdownZIndex } from '../../styles/z-index'

interface ActionItem {
  label: string
  icon: React.ComponentType<{ size?: number; className?: string }>
  onClick: () => void
  variant?: 'default' | 'destructive'
  disabled?: boolean
  hasSeparatorAfter?: boolean
}

interface ActionsDropdownProps {
  actions: ActionItem[]
  disabled?: boolean
  className?: string
}

export function ActionsDropdown({
  actions,
  className,
  disabled,
}: ActionsDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const dropdownRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const modalDepth = useModalDepth()
  const zIndex = React.useMemo(
    () => getDropdownZIndex(modalDepth),
    [modalDepth]
  )

  useEffect(() => {
    if (!isOpen || !triggerRef.current) return

    const updatePosition = () => {
      const rect = triggerRef.current!.getBoundingClientRect()
      const dropdownWidth = 120
      const dropdownHeight = actions.length * 32 + 16

      let left = rect.left
      let top = rect.top

      if (left + dropdownWidth > window.innerWidth) {
        left = rect.right - dropdownWidth
      }
      if (top + dropdownHeight > window.innerHeight) {
        top = rect.bottom - dropdownHeight
      }
      if (top < 0) {
        top = rect.bottom + 4
      }

      setPosition({ top, left })
    }

    const handleClickOutside = (e: MouseEvent) => {
      if (
        !dropdownRef.current?.contains(e.target as Node) &&
        !triggerRef.current?.contains(e.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    updatePosition()
    document.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [isOpen, actions.length])

  if (!isOpen || disabled)
    return (
      <div
        className={cn('relative flex justify-end', className, {
          'opacity-50 cursor-not-allowed': disabled,
        })}
      >
        <button
          ref={triggerRef}
          onClick={(e) => {
            e.stopPropagation()
            setIsOpen(true)
          }}
          className={cn(
            'flex items-center justify-center w-5 h-5 text-gray-500 hover:text-gray-700 focus:outline-none focus:text-gray-700',
            {
              'cursor-not-allowed': disabled,
            }
          )}
          aria-label="Actions"
        >
          <MoreVertical
            className={cn({
              'cursor-not-allowed': disabled,
            })}
            size={20}
          />
        </button>
      </div>
    )

  return (
    <>
      <div className={cn('relative flex justify-end', className)}>
        <button
          ref={triggerRef}
          onClick={(e) => {
            e.stopPropagation()
            setIsOpen(false)
          }}
          className="flex items-center justify-center w-5 h-5 text-gray-500 hover:text-gray-700 focus:outline-none focus:text-gray-700"
          aria-label="Actions"
        >
          <MoreVertical size={20} />
        </button>
      </div>
      {createPortal(
        <div
          ref={dropdownRef}
          className="fixed bg-white rounded-lg border w-[120px] shadow-lg border-[#8EA69B] p-2 space-y-2"
          style={{
            top: position.top,
            left: position.left,
            zIndex,
            boxShadow: '0px 4px 4px 0px rgba(5, 70, 40, 0.15)',
          }}
        >
          {actions.map((action, index) => (
            <div key={index}>
              <button
                disabled={action.disabled}
                className={cn(
                  'w-full flex items-center text-sm text-left transition-colors rounded p-1 gap-2',
                  'focus:outline-none',
                  action.disabled
                    ? 'opacity-50 cursor-not-allowed'
                    : 'cursor-pointer text-gray-900',
                  action.hasSeparatorAfter && 'mb-2',
                  hoveredIndex === index && !action.disabled && 'bg-[#E6F1EC]'
                )}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                onClick={(e) => {
                  e.stopPropagation()
                  if (!action.disabled) {
                    action.onClick()
                    setIsOpen(false)
                  }
                }}
              >
                <action.icon
                  size={20}
                  className="flex-shrink-0 text-gray-500"
                />
                <span className="truncate">{action.label}</span>
              </button>
              {action.hasSeparatorAfter && (
                <div className="border-t border-[#E5E7EB]" />
              )}
            </div>
          ))}
        </div>,
        document.body
      )}
    </>
  )
}

// Pre-configured actions for common use cases
export const createEditAction = (onClick: () => void): ActionItem => ({
  label: 'Edit',
  icon: Pencil,
  onClick,
})

export const createCopyAction = (onClick: () => void): ActionItem => ({
  label: 'Copy',
  icon: Copy,
  onClick,
})

export const createPauseAction = (onClick: () => void): ActionItem => ({
  label: 'Pause',
  icon: Pause,
  onClick,
})

export const createPlayAction = (onClick: () => void): ActionItem => ({
  label: 'Resume',
  icon: Play,
  onClick,
})

export const createToggleActiveAction = (
  onClick: () => void,
  isActive: boolean,
  isDisabled = false
): ActionItem => ({
  label: isActive ? 'Disable' : 'Enable',
  icon: isActive ? ZapOff : Zap,
  onClick,
  disabled: isDisabled,
  hasSeparatorAfter: true,
})

export const createDeleteAction = (onClick: () => void): ActionItem => ({
  label: 'Delete',
  icon: Trash2,
  onClick,
  variant: 'destructive' as const,
})
